'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { sanitizeFormData } from '@/lib/security/sanitize';

interface PartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  // Company Details
  companyName: string;
  companyWebsite: string;
  industry: string;
  companySize: string;
  country: string;
  // Contact Details
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactDesignation: string;
  // Manufacturing Requirements
  manufacturingCapabilities: string[];
  annualVolume: string;
  projectTimeline: string;
  qualityCertifications: string;
  // Partnership Interest
  partnershipType: string;
  projectDescription: string;
  additionalNotes: string;
}

const INITIAL_FORM: FormData = {
  companyName: '',
  companyWebsite: '',
  industry: '',
  companySize: '',
  country: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contactDesignation: '',
  manufacturingCapabilities: [],
  annualVolume: '',
  projectTimeline: '',
  qualityCertifications: '',
  partnershipType: '',
  projectDescription: '',
  additionalNotes: '',
};

const INDUSTRIES = [
  'Aerospace & Defense',
  'Space Systems',
  'Medical Devices',
  'Automotive',
  'Industrial Equipment',
  'Electronics & Semiconductors',
  'Energy & Power',
  'Maritime & Naval',
  'Other',
];

const COMPANY_SIZES = [
  '1–50 employees',
  '51–200 employees',
  '201–500 employees',
  '501–1000 employees',
  '1000+ employees',
];

const CAPABILITIES = [
  'Precision CNC Machining',
  'Sheet Metal Fabrication',
  'Structural Manufacturing',
  'Tooling & Fixtures',
  'Sub-Assemblies',
  'Additive Manufacturing',
  'Composite Structures',
  'Surface Treatment',
  'Quality Inspection',
  'Production Recovery',
];

const TIMELINES = [
  'Immediate (< 4 weeks)',
  'Short-term (1–3 months)',
  'Mid-term (3–6 months)',
  'Long-term (6–12 months)',
  'Strategic (12+ months)',
];

const PARTNERSHIP_TYPES = [
  'Tier-1 Supply Chain Partner',
  'Tier-2 Sub-Contractor',
  'Co-Development Partner',
  'Capacity Augmentation',
  'Emergency Production Support',
  'Long-term Strategic Alliance',
];

const STEPS = [
  { id: 1, label: 'Company', icon: 'BuildingOffice2Icon' },
  { id: 2, label: 'Contact', icon: 'UserCircleIcon' },
  { id: 3, label: 'Requirements', icon: 'CogIcon' },
  { id: 4, label: 'Partnership', icon: 'HandshakeIcon' },
];

const inputClass =
  'w-full px-4 py-2.5 bg-muted/30 border border-border/50 rounded-lg font-body text-sm text-foreground placeholder-technical-gray/50 focus:outline-none focus:border-accent/60 focus:bg-muted/50 transition-all duration-200';

export default function PartnerModal({ isOpen, onClose }: PartnerModalProps) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setForm(INITIAL_FORM);
        setSubmitted(false);
        setError(null);
      }, 300);
    }
  }, [isOpen]);

  const updateField = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCapability = (cap: string) => {
    setForm((prev) => ({
      ...prev,
      manufacturingCapabilities: prev.manufacturingCapabilities.includes(cap)
        ? prev.manufacturingCapabilities.filter((c) => c !== cap)
        : [...prev.manufacturingCapabilities, cap],
    }));
  };

  const isStepValid = () => {
    if (step === 1) return form.companyName && form.industry && form.companySize && form.country;
    if (step === 2) return form.contactName && form.contactEmail;
    if (step === 3) return form.manufacturingCapabilities.length > 0 && form.projectTimeline;
    if (step === 4) return form.partnershipType && form.projectDescription;
    return false;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    // Sanitize all text fields before submission
    const textFields: Record<string, string> = {
      companyName: form.companyName,
      companyWebsite: form.companyWebsite,
      industry: form.industry,
      companySize: form.companySize,
      country: form.country,
      contactName: form.contactName,
      contactEmail: form.contactEmail,
      contactPhone: form.contactPhone,
      contactDesignation: form.contactDesignation,
      annualVolume: form.annualVolume,
      projectTimeline: form.projectTimeline,
      qualityCertifications: form.qualityCertifications,
      partnershipType: form.partnershipType,
      projectDescription: form.projectDescription,
      additionalNotes: form.additionalNotes,
    };
    const { sanitized, errors: sanitizeErrors } = sanitizeFormData(textFields);
    if (Object.keys(sanitizeErrors).length > 0) {
      setError('Invalid input detected. Please review your entries and try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('partner_submissions').insert({
        company_name: sanitized.companyName,
        company_website: sanitized.companyWebsite || null,
        industry: sanitized.industry,
        company_size: sanitized.companySize,
        country: sanitized.country,
        contact_name: sanitized.contactName,
        contact_email: sanitized.contactEmail,
        contact_phone: sanitized.contactPhone || null,
        contact_designation: sanitized.contactDesignation || null,
        manufacturing_capabilities: form.manufacturingCapabilities,
        annual_volume: sanitized.annualVolume || null,
        project_timeline: sanitized.projectTimeline,
        quality_certifications: sanitized.qualityCertifications || null,
        partnership_type: sanitized.partnershipType,
        project_description: sanitized.projectDescription,
        additional_notes: sanitized.additionalNotes || null,
        status: 'new',
      });

      if (dbError) {
        setError('Failed to submit. Please try again or contact us directly.');
        return;
      }

      // Send confirmation + team notification emails via Edge Function
      try {
        const { data: emailData, error: emailError } = await supabase.functions.invoke('send-partner-emails', {
          body: {
            companyName: sanitized.companyName,
            companyWebsite: sanitized.companyWebsite,
            industry: sanitized.industry,
            companySize: sanitized.companySize,
            country: sanitized.country,
            contactName: sanitized.contactName,
            contactEmail: sanitized.contactEmail,
            contactPhone: sanitized.contactPhone,
            contactDesignation: sanitized.contactDesignation,
            manufacturingCapabilities: form.manufacturingCapabilities,
            annualVolume: sanitized.annualVolume,
            projectTimeline: sanitized.projectTimeline,
            qualityCertifications: sanitized.qualityCertifications,
            partnershipType: sanitized.partnershipType,
            projectDescription: sanitized.projectDescription,
            additionalNotes: sanitized.additionalNotes,
          },
        });
        if (emailError) {
          console.error('[PartnerModal] Edge function error:', emailError);
        } else {
          console.log('[PartnerModal] Email function response:', emailData);
        }
      } catch (emailErr) {
        // Email sending failure is non-blocking — submission is already saved
        console.error('[PartnerModal] Email invocation exception:', emailErr);
      }

      setSubmitted(true);
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Become a Manufacturing Partner"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-gradient-to-b from-[#0a1628] to-[#050a14] border border-accent/20 rounded-2xl shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="relative flex-shrink-0 px-8 pt-8 pb-6 border-b border-border/40">
          <div className="absolute inset-0 opacity-10 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at top left, #ee9572 0%, transparent 60%)' }}
          />
          <div className="flex items-start justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/10 border border-accent/30 rounded-full mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                <span className="font-cta text-xs text-accent tracking-widest">PARTNERSHIP INQUIRY</span>
              </div>
              <h2 className="font-headline text-2xl font-bold text-foreground leading-tight">
                Become a Manufacturing Partner
              </h2>
              <p className="font-body text-sm text-technical-gray mt-1">
                Tell us about your requirements — we respond within 24 hours.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-technical-gray hover:text-foreground hover:bg-muted/50 transition-all duration-200 flex-shrink-0 ml-4"
              aria-label="Close modal"
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>

          {/* Step Indicators */}
          <div className="flex items-center gap-2 mt-6">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="flex items-center gap-2 flex-1">
                <button
                  onClick={() => step > s.id && setStep(s.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200 text-xs font-cta tracking-wide flex-1 justify-center
                    ${step === s.id
                      ? 'bg-accent/20 border border-accent/50 text-accent'
                      : step > s.id
                      ? 'bg-aerospace-green/10 border border-aerospace-green/30 text-aerospace-green cursor-pointer hover:bg-aerospace-green/20' :'bg-muted/30 border border-border/30 text-technical-gray cursor-default'
                    }`}
                >
                  {step > s.id ? (
                    <Icon name="CheckIcon" size={12} />
                  ) : (
                    <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[10px]">{s.id}</span>
                  )}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {idx < STEPS.length - 1 && (
                  <div className={`h-px w-4 flex-shrink-0 transition-colors duration-300 ${step > s.id ? 'bg-aerospace-green/40' : 'bg-border/30'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {submitted ? (
            <SuccessView onClose={onClose} contactName={form.contactName} companyName={form.companyName} />
          ) : (
            <>
              {step === 1 && <StepCompany form={form} updateField={updateField} />}
              {step === 2 && <StepContact form={form} updateField={updateField} />}
              {step === 3 && <StepRequirements form={form} updateField={updateField} toggleCapability={toggleCapability} />}
              {step === 4 && <StepPartnership form={form} updateField={updateField} />}
            </>
          )}

          {error && (
            <div className="mt-4 flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <Icon name="ExclamationTriangleIcon" size={16} className="text-red-400 flex-shrink-0" />
              <p className="font-body text-sm text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="flex-shrink-0 flex items-center justify-between px-8 py-5 border-t border-border/40 bg-black/20">
            <button
              onClick={() => step > 1 ? setStep(step - 1) : onClose()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-border/50 text-technical-gray hover:text-foreground hover:border-accent/40 transition-all duration-200 font-cta text-sm"
            >
              <Icon name="ChevronLeftIcon" size={16} />
              {step > 1 ? 'Back' : 'Cancel'}
            </button>

            <div className="flex items-center gap-3">
              <span className="font-body text-xs text-technical-gray">Step {step} of {STEPS.length}</span>
              {step < STEPS.length ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={!isStepValid()}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-mission-orange text-primary font-cta text-sm font-medium transition-all duration-200 hover:shadow-glow hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  Continue
                  <Icon name="ChevronRightIcon" size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!isStepValid() || isSubmitting}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-mission-orange text-primary font-cta text-sm font-medium transition-all duration-200 hover:shadow-glow hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Inquiry
                      <Icon name="PaperAirplaneIcon" size={16} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 1: Company Details ───────────────────────────────────────────────
function StepCompany({ form, updateField }: { form: FormData; updateField: (f: keyof FormData, v: string) => void }) {
  return (
    <div className="space-y-5">
      <StepHeader
        icon="BuildingOffice2Icon"
        title="Company Details"
        subtitle="Tell us about your organization"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Company Name" required>
          <input
            type="text"
            value={form.companyName}
            onChange={(e) => updateField('companyName', e.target.value)}
            placeholder="Wings Bharath Pvt. Ltd."
            className={inputClass}
          />
        </FormField>
        <FormField label="Company Website">
          <input
            type="url"
            value={form.companyWebsite}
            onChange={(e) => updateField('companyWebsite', e.target.value)}
            placeholder="https://yourcompany.com"
            className={inputClass}
          />
        </FormField>
      </div>
      <FormField label="Industry" required>
        <select value={form.industry} onChange={(e) => updateField('industry', e.target.value)} className={inputClass}>
          <option value="">Select your industry</option>
          {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Company Size" required>
          <select value={form.companySize} onChange={(e) => updateField('companySize', e.target.value)} className={inputClass}>
            <option value="">Select size</option>
            {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Country / Region" required>
          <input
            type="text"
            value={form.country}
            onChange={(e) => updateField('country', e.target.value)}
            placeholder="India, USA, UK..."
            className={inputClass}
          />
        </FormField>
      </div>
    </div>
  );
}

// ─── Step 2: Contact Details ───────────────────────────────────────────────
function StepContact({ form, updateField }: { form: FormData; updateField: (f: keyof FormData, v: string) => void }) {
  return (
    <div className="space-y-5">
      <StepHeader
        icon="UserCircleIcon"
        title="Contact Information"
        subtitle="Who should we reach out to?"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Full Name" required>
          <input
            type="text"
            value={form.contactName}
            onChange={(e) => updateField('contactName', e.target.value)}
            placeholder="John Smith"
            className={inputClass}
          />
        </FormField>
        <FormField label="Designation">
          <input
            type="text"
            value={form.contactDesignation}
            onChange={(e) => updateField('contactDesignation', e.target.value)}
            placeholder="VP Engineering, Procurement Head..."
            className={inputClass}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Business Email" required>
          <input
            type="email"
            value={form.contactEmail}
            onChange={(e) => updateField('contactEmail', e.target.value)}
            placeholder="john@company.com"
            className={inputClass}
          />
        </FormField>
        <FormField label="Phone Number">
          <input
            type="tel"
            value={form.contactPhone}
            onChange={(e) => updateField('contactPhone', e.target.value)}
            placeholder="+91 98765 43210"
            className={inputClass}
          />
        </FormField>
      </div>
      <div className="flex items-start gap-3 p-4 bg-accent/5 border border-accent/20 rounded-lg">
        <Icon name="ShieldCheckIcon" size={16} className="text-accent flex-shrink-0 mt-0.5" />
        <p className="font-body text-xs text-technical-gray leading-relaxed">
          Your contact information is kept strictly confidential and used only to respond to your partnership inquiry. We do not share your data with third parties.
        </p>
      </div>
    </div>
  );
}

// ─── Step 3: Manufacturing Requirements ───────────────────────────────────
function StepRequirements({
  form,
  updateField,
  toggleCapability,
}: {
  form: FormData;
  updateField: (f: keyof FormData, v: string) => void;
  toggleCapability: (cap: string) => void;
}) {
  return (
    <div className="space-y-5">
      <StepHeader
        icon="CogIcon"
        title="Manufacturing Requirements"
        subtitle="What capabilities do you need?"
      />
      <FormField label="Required Capabilities" required hint="Select all that apply">
        <div className="grid grid-cols-2 gap-2 mt-1">
          {CAPABILITIES.map((cap) => {
            const selected = form.manufacturingCapabilities.includes(cap);
            return (
              <button
                key={cap}
                type="button"
                onClick={() => toggleCapability(cap)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all duration-200 font-body text-xs
                  ${selected
                    ? 'bg-accent/15 border-accent/60 text-accent' :'bg-muted/20 border-border/40 text-technical-gray hover:border-accent/30 hover:text-foreground'
                  }`}
              >
                <span className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors
                  ${selected ? 'bg-accent border-accent' : 'border-border/60'}`}>
                  {selected && <Icon name="CheckIcon" size={10} className="text-primary" />}
                </span>
                {cap}
              </button>
            );
          })}
        </div>
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Project Timeline" required>
          <select value={form.projectTimeline} onChange={(e) => updateField('projectTimeline', e.target.value)} className={inputClass}>
            <option value="">Select timeline</option>
            {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </FormField>
        <FormField label="Annual Volume Estimate">
          <input
            type="text"
            value={form.annualVolume}
            onChange={(e) => updateField('annualVolume', e.target.value)}
            placeholder="e.g. 500 units/year, $2M spend"
            className={inputClass}
          />
        </FormField>
      </div>
      <FormField label="Quality Certifications Required">
        <input
          type="text"
          value={form.qualityCertifications}
          onChange={(e) => updateField('qualityCertifications', e.target.value)}
          placeholder="AS9100, ITAR, ISO 9001, NADCAP..."
          className={inputClass}
        />
      </FormField>
    </div>
  );
}

// ─── Step 4: Partnership Interest ─────────────────────────────────────────
function StepPartnership({ form, updateField }: { form: FormData; updateField: (f: keyof FormData, v: string) => void }) {
  return (
    <div className="space-y-5">
      <StepHeader
        icon="RocketLaunchIcon"
        title="Partnership Interest"
        subtitle="How would you like to work together?"
      />
      <FormField label="Partnership Type" required>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
          {PARTNERSHIP_TYPES.map((type) => {
            const selected = form.partnershipType === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => updateField('partnershipType', type)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all duration-200 font-body text-xs
                  ${selected
                    ? 'bg-mission-orange/15 border-mission-orange/60 text-mission-orange' :'bg-muted/20 border-border/40 text-technical-gray hover:border-mission-orange/30 hover:text-foreground'
                  }`}
              >
                <span className={`w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center transition-colors
                  ${selected ? 'bg-mission-orange border-mission-orange' : 'border-border/60'}`}>
                  {selected && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                </span>
                {type}
              </button>
            );
          })}
        </div>
      </FormField>
      <FormField label="Project Description" required hint="Briefly describe your project or manufacturing need">
        <textarea
          value={form.projectDescription}
          onChange={(e) => updateField('projectDescription', e.target.value)}
          placeholder="Describe your project requirements, specifications, quantities, and any critical constraints..."
          rows={4}
          className={`${inputClass} resize-none`}
        />
      </FormField>
      <FormField label="Additional Notes">
        <textarea
          value={form.additionalNotes}
          onChange={(e) => updateField('additionalNotes', e.target.value)}
          placeholder="Any other information you'd like to share..."
          rows={2}
          className={`${inputClass} resize-none`}
        />
      </FormField>
    </div>
  );
}

// ─── Success View ──────────────────────────────────────────────────────────
function SuccessView({ onClose, contactName, companyName }: { onClose: () => void; contactName: string; companyName: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center space-y-6">
      <div className="relative">
        <div className="w-20 h-20 rounded-full bg-aerospace-green/10 border border-aerospace-green/30 flex items-center justify-center">
          <Icon name="CheckBadgeIcon" size={40} className="text-aerospace-green" />
        </div>
        <div className="absolute inset-0 rounded-full bg-aerospace-green/5 animate-ping" />
      </div>
      <div className="space-y-2">
        <h3 className="font-headline text-2xl font-bold text-foreground">Inquiry Submitted!</h3>
        <p className="font-body text-technical-gray max-w-sm">
          Thank you, <span className="text-accent font-medium">{contactName}</span>. We've received your partnership inquiry from{' '}
          <span className="text-accent font-medium">{companyName}</span>.
        </p>
      </div>
      <div className="flex flex-col gap-3 w-full max-w-xs">
        <div className="flex items-center gap-3 px-4 py-3 bg-accent/5 border border-accent/20 rounded-lg text-left">
          <Icon name="ClockIcon" size={16} className="text-accent flex-shrink-0" />
          <p className="font-body text-xs text-technical-gray">Our team will respond within <span className="text-foreground font-medium">24 business hours</span></p>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 bg-accent/5 border border-accent/20 rounded-lg text-left">
          <Icon name="EnvelopeIcon" size={16} className="text-accent flex-shrink-0" />
          <p className="font-body text-xs text-technical-gray">A confirmation has been noted for your reference</p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="px-8 py-3 bg-mission-orange text-primary font-cta text-sm font-medium rounded-lg hover:shadow-glow hover:-translate-y-0.5 transition-all duration-200"
      >
        Close
      </button>
    </div>
  );
}

// ─── Shared Helpers ────────────────────────────────────────────────────────
function StepHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center flex-shrink-0">
        <Icon name={icon as any} size={20} className="text-accent" />
      </div>
      <div>
        <h3 className="font-headline text-lg font-bold text-foreground">{title}</h3>
        <p className="font-body text-xs text-technical-gray">{subtitle}</p>
      </div>
    </div>
  );
}

function FormField({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1 font-cta text-xs text-technical-gray tracking-wide uppercase">
        {label}
        {required && <span className="text-mission-orange">*</span>}
        {hint && <span className="normal-case text-[10px] text-technical-gray/60 ml-1">({hint})</span>}
      </label>
      {children}
    </div>
  );
}
